import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, ArrowLeft, Check, FileText, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui-kit/button';
import { useTranslation } from 'react-i18next';
import { Turnstile } from 'react-turnstile';
type GenerationSteps = {
  title: string;
  description: string;
  progress: number;
  duration: number;
};

interface LoadingScreenProps {
  currentStage: GenerationSteps;
  isTokenExpired: boolean;
  retryTurnstileStatus: 'required' | 'success' | 'error' | 'expired';
  retryTurnstileToken: string | null;
  setRetryTurnstileStatus: (status: 'required' | 'success' | 'error' | 'expired') => void;
  setRetryTurnstileToken: (token: string | null) => void;
  retryGeneration: (token: string) => void;
  generationError: string | null;
  setGenerationError: (error: string | null) => void;
  handleRetryGeneration: () => void;
  handleSubmit: () => void;
  displayedProgress: number;
  generationStepIndex: number;
  generationSteps: GenerationSteps[];
  currentTipIndex: number;
  generationTips: string[];
  fakeProgress: number;
}
const LoadingScreen = ({
  currentStage,
  isTokenExpired,
  setRetryTurnstileStatus,
  setRetryTurnstileToken,
  retryGeneration,
  generationError,
  setGenerationError,
  handleRetryGeneration,
  handleSubmit,
  displayedProgress,
  generationStepIndex,
  retryTurnstileStatus,
  retryTurnstileToken,
  generationSteps,
  currentTipIndex,
  generationTips,
  fakeProgress,
}: LoadingScreenProps) => {
  const { t } = useTranslation();

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-[hsl(var(--bg))] px-3 sm:px-4 py-6 sm:py-0">
      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,hsla(var(--brand-primary)/0.14),transparent_65%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_bottom,hsla(var(--brand-primary)/0.08),transparent_60%)]" />

      <div className="relative z-10 w-full max-w-2xl">
        {/* Token Expiration - Show Turnstile Widget */}
        {isTokenExpired ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl sm:rounded-3xl border border-amber-200 bg-background p-5 sm:p-8 shadow-xl text-center"
          >
            <div className="mx-auto mb-4 sm:mb-6 flex h-14 w-14 sm:h-20 sm:w-20 items-center justify-center rounded-2xl sm:rounded-3xl bg-amber-50">
              <AlertTriangle className="h-7 w-7 sm:h-10 sm:w-10 text-amber-600" />
            </div>
            <h2 className="text-lg sm:text-2xl font-semibold text-[hsl(var(--fg))] mb-2 sm:mb-3 font-heading">
              {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_VERIFICATIONEXPIREDTITLE')}
            </h2>
            <p className="text-sm sm:text-base text-brand-muted mb-4 sm:mb-6 leading-relaxed">
              {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_VERIFICATIONEXPIREDDESCRIPTION')}.
            </p>

            {/* Inline Turnstile Widget */}
            <div className="flex flex-col items-center gap-4 mb-6">
              {import.meta.env.VITE_TURNSTILE_SITE_KEY ? (
                <>
                  <Turnstile
                    sitekey={import.meta.env.VITE_TURNSTILE_SITE_KEY as string}
                    retry="auto"
                    refreshExpired="auto"
                    onError={() => {
                      setRetryTurnstileStatus('error');
                      setRetryTurnstileToken(null);
                    }}
                    onExpire={() => {
                      setRetryTurnstileStatus('expired');
                      setRetryTurnstileToken(null);
                    }}
                    onLoad={() => {
                      setRetryTurnstileStatus('required');
                    }}
                    onVerify={(token: string) => {
                      setRetryTurnstileStatus('success');
                      setRetryTurnstileToken(token);
                      // Automatically retry generation with new token
                      retryGeneration(token);
                    }}
                  />
                  {retryTurnstileStatus === 'success' && retryTurnstileToken && (
                    <div className="flex items-center justify-center gap-2 text-sm text-emerald-600">
                      <Check className="h-4 w-4" />
                      <span>
                        {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_VERIFICATIONCOMPLETE')}
                      </span>
                    </div>
                  )}
                  {retryTurnstileStatus === 'error' && (
                    <div className="flex items-center justify-center gap-2 text-sm text-red-600">
                      <AlertTriangle className="h-4 w-4" />
                      <span>
                        {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_VERIFICATIONFAILED')}
                      </span>
                    </div>
                  )}
                  {retryTurnstileStatus === 'expired' && (
                    <div className="flex items-center justify-center gap-2 text-sm text-amber-600">
                      <AlertTriangle className="h-4 w-4" />
                      <span>
                        {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_VERIFICATIONEXPIRED')}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
                  <AlertTriangle className="mx-auto h-5 w-5 text-amber-600 mb-2" />
                  <p className="text-sm text-amber-800 font-medium mb-1">
                    {t('EMPLOYMENTAGREEMENT_SMARTFLOW_WELCOME_TURNSTILECONFIGMISSING')}
                  </p>
                  <p className="text-xs text-amber-700">
                    {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_TURNSTILECONFIGDESCRIPTION')}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetryGeneration}
                className="flex items-center gap-2 w-full sm:w-auto"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_GOBACK')}
              </Button>
            </div>
          </motion.div>
        ) : generationError ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl sm:rounded-3xl border border-red-200 bg-background p-5 sm:p-8 shadow-xl text-center"
          >
            <div className="mx-auto mb-4 sm:mb-6 flex h-14 w-14 sm:h-20 sm:w-20 items-center justify-center rounded-2xl sm:rounded-3xl bg-red-50">
              <AlertTriangle className="h-7 w-7 sm:h-10 sm:w-10 text-red-500" />
            </div>
            <h2 className="text-lg sm:text-2xl font-semibold text-[hsl(var(--fg))] mb-2 sm:mb-3 font-heading">
              {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_GENERATIONFAILED')}
            </h2>
            <p className="text-sm sm:text-base text-brand-muted mb-4 sm:mb-6 leading-relaxed">
              {generationError}
            </p>
            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-center gap-2 sm:gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetryGeneration}
                className="flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_GOBACK')}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setGenerationError(null);
                  handleSubmit();
                }}
                className="flex items-center justify-center gap-2 bg-brand-primary w-full sm:w-auto"
              >
                {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_TRYAGAIN')}
              </Button>
            </div>
          </motion.div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="mb-6 sm:mb-12 space-y-3 sm:space-y-5 text-center py-2 sm:py-4"
            >
              {/* Animated icon */}
              <motion.div
                className="mx-auto flex h-16 w-16 sm:h-24 sm:w-24 items-center justify-center rounded-2xl sm:rounded-3xl border-2 border-[hsl(var(--brand-primary)/0.2)] bg-linear-to-br from-[hsl(var(--brand-primary)/0.1)] to-[hsl(var(--brand-primary)/0.05)] shadow-lg backdrop-blur-sm"
                animate={{
                  scale: [1, 1.05, 1],
                  boxShadow: [
                    '0 10px 25px -5px hsla(var(--brand-primary)/0.1)',
                    '0 15px 35px -5px hsla(var(--brand-primary)/0.2)',
                    '0 10px 25px -5px hsla(var(--brand-primary)/0.1)',
                  ],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <div className="animate-pulse">
                  <FileText className="h-8 w-8 sm:h-12 sm:w-12 text-brand-primary" />
                </div>
              </motion.div>

              <h2 className="text-2xl sm:text-4xl md:text-5xl font-semibold text-[hsl(var(--fg))] font-heading tracking-tight">
                {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_TITLE')}
              </h2>
              <p className="text-sm sm:text-lg text-brand-muted leading-relaxed">
                {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_SUBTITLE')}
              </p>
            </motion.div>

            {/* Progress Section */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="rounded-2xl sm:rounded-3xl border border-brand-border bg-background p-4 sm:p-8 shadow-xl backdrop-blur-sm"
            >
              {/* Progress header */}
              <div className="mb-3 sm:mb-4 flex items-center justify-between gap-2 text-xs sm:text-sm font-medium">
                <span className="text-[hsl(var(--fg))]">
                  {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_PERCENTCOMPLETE', {
                    percent: displayedProgress,
                  })}
                </span>
                <span className="text-brand-muted">
                  {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_STEPXOFY', {
                    current: generationStepIndex + 1,
                    total: generationSteps.length,
                  })}
                </span>
              </div>

              {/* Progress bar */}
              <div className="relative h-4 overflow-hidden rounded-full border border-[hsl(var(--brand-primary)/0.25)] bg-[hsl(var(--brand-primary)/0.08)] shadow-inner">
                {/* Animated background shimmer */}
                <motion.div
                  className="absolute inset-0 bg-linear-to-r from-transparent via-[hsl(var(--brand-primary)/0.15)] to-transparent"
                  animate={{
                    x: ['-100%', '200%'],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                />
                {/* Progress fill */}
                <motion.div
                  className="absolute left-0 top-0 h-full rounded-full bg-linear-to-r from-brand-primary via-[hsl(var(--brand-primary)/0.9)] to-brand-primary shadow-lg"
                  style={{ width: `${fakeProgress}%` }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                />
                {/* Glowing edge */}
                <motion.div
                  className="absolute left-0 top-0 h-full w-2 rounded-full bg-white/60 blur-sm"
                  style={{ left: `${fakeProgress}%`, marginLeft: '-4px' }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                />
                {/* Moving dot indicator */}
                <motion.div
                  className="absolute top-1/2 h-5 w-5 -translate-y-1/2 rounded-full border-2 border-white bg-brand-primary shadow-[0_0_0_3px_rgba(255,255,255,0.5),0_4px_12px_hsla(206,100%,35%,0.5)]"
                  style={{ left: `${fakeProgress}%`, marginLeft: '-10px' }}
                  animate={{
                    scale: [1, 1.15, 1],
                    boxShadow: [
                      '0 0 0 3px rgba(255,255,255,0.5), 0 4px 12px hsla(206,100%,35%,0.5)',
                      '0 0 0 4px rgba(255,255,255,0.6), 0 6px 16px hsla(206,100%,35%,0.6)',
                      '0 0 0 3px rgba(255,255,255,0.5), 0 4px 12px hsla(206,100%,35%,0.5)',
                    ],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              </div>

              {/* Current stage info */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={generationStepIndex}
                  initial={{ opacity: 0, y: 10, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.98 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  className="rounded-xl sm:rounded-2xl border border-brand-border bg-linear-to-br from-background to-[hsl(var(--brand-primary)/0.02)] p-4 sm:p-8 shadow-lg backdrop-blur-sm mt-4 sm:mt-6"
                >
                  <div className="flex items-start gap-3 sm:gap-5">
                    <div className="flex h-10 w-10 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-lg sm:rounded-xl border-2 border-[hsl(var(--brand-primary)/0.2)] bg-linear-to-br from-[hsl(var(--brand-primary)/0.1)] to-[hsl(var(--brand-primary)/0.05)] shadow-sm">
                      <div className="animate-pulse">
                        <Sparkles className="h-5 w-5 sm:h-7 sm:w-7 text-brand-primary" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="mb-1 sm:mb-2 text-base sm:text-xl font-semibold text-[hsl(var(--fg))] font-heading tracking-tight">
                        {currentStage.title}
                      </h3>
                      <p className="leading-relaxed text-brand-muted text-sm sm:text-base">
                        {currentStage.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Stage checklist */}
              <div className="mt-5 sm:mt-7 space-y-2 sm:space-y-3">
                {generationSteps.map((stage, index) => {
                  const isCompleted = index < generationStepIndex;
                  const isCurrent = index === generationStepIndex;

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`flex items-center gap-2.5 sm:gap-3 text-xs sm:text-sm transition-all duration-300 ${isCompleted
                        ? 'text-brand-primary'
                        : isCurrent
                          ? 'text-[hsl(var(--fg))] font-medium'
                          : 'text-muted-foreground'
                        }`}
                    >
                      {isCompleted ? (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{
                            type: 'spring',
                            stiffness: 500,
                            damping: 30,
                          }}
                          className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-white"
                        >
                          <Check className="h-3 w-3" />
                        </motion.div>
                      ) : isCurrent ? (
                        <motion.div
                          animate={{
                            scale: [1, 1.3, 1],
                            opacity: [1, 0.8, 1],
                          }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: 'easeInOut',
                          }}
                          className="h-5 w-5 rounded-full border-2 border-brand-primary bg-[hsl(var(--brand-primary)/0.1)]"
                        />
                      ) : (
                        <div className="h-5 w-5 rounded-full border-2 border-brand-border bg-[hsl(var(--brand-border)/0.3)]" />
                      )}
                      <span className={isCurrent ? 'font-medium' : ''}>{stage.title}</span>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* Bottom hint */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-4 sm:mt-8 text-center text-xs sm:text-sm text-brand-muted"
            >
              {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_TYPICALLYTAKES')}
            </motion.p>

            {/* Tips carousel */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mt-4 sm:mt-6 rounded-xl border border-brand-border bg-brand-surface p-3 sm:p-4 mb-4 sm:mb-6!"
            >
              <div className="flex items-start gap-2.5 sm:gap-3">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[hsl(var(--brand-primary))]/10 flex items-center justify-center shrink-0">
                  <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-brand-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] sm:text-xs font-semibold text-brand-primary uppercase tracking-wide mb-0.5 sm:mb-1">
                    {t('EMPLOYMENTAGREEMENT_SMARTFLOW_GENERATION_DIDYOUKNOW')}
                  </p>
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={currentTipIndex}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      transition={{ duration: 0.3 }}
                      className="text-xs sm:text-sm text-brand-muted leading-relaxed"
                    >
                      {generationTips[currentTipIndex]}
                    </motion.p>
                  </AnimatePresence>
                </div>
              </div>
              {/* Tip indicator dots */}
              <div className="flex items-center justify-center gap-1.5 mt-2 sm:mt-3">
                {generationTips.map((_, index) => (
                  <div
                    key={index}
                    className={`h-1.5 rounded-full transition-all ${index === currentTipIndex
                      ? 'w-4 bg-brand-primary'
                      : 'w-1.5 bg-brand-border'
                      }`}
                  />
                ))}
              </div>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
};

export default LoadingScreen;
